import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Check, Pencil } from "lucide-react";
import { useEffect, useState } from "react";

import { useChatContext } from "@/features/chat/context/ChatContext";
import {
  DashboardGrid,
  dashboardSearchSchema,
  FilterSheet,
  useDashboardBySlugQuery,
} from "@/features/dashboard";
import { AgentInput } from "@/features/dashboard/ui/AgentInput";
import { usePageHeader } from "@/shared/store/page-header";
import { Button } from "@/shared/ui";

export const Route = createFileRoute("/_authenticated/dashboard/$slug")({
  validateSearch: dashboardSearchSchema,
  component: DynamicDashboardPage,
});

function DynamicDashboardPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { data: dashboard, isLoading, error } = useDashboardBySlugQuery(slug);
  const { setTitle, reset } = usePageHeader();
  const { isOpen: isChatOpen } = useChatContext();
  const [isEditMode, setIsEditMode] = useState(false);

  // Check if dashboard has date-range filter
  const hasDateRangeFilter = dashboard?.filterConfig?.some((f) => f.type === "date-range") ?? false;

  // Check if filters are ready (dates are set if date-range filter exists)
  const filtersReady = !hasDateRangeFilter || (!!search.startDate && !!search.endDate);

  // Set default date range (last 6 months) if no dates are set
  useEffect(() => {
    if (dashboard && hasDateRangeFilter && !search.startDate && !search.endDate) {
      const today = new Date();
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(today.getMonth() - 6);

      void navigate({
        to: ".",
        search: {
          startDate: sixMonthsAgo.toISOString().split("T")[0],
          endDate: today.toISOString().split("T")[0],
        },
        replace: true,
      });
    }
  }, [dashboard, hasDateRangeFilter, search.startDate, search.endDate, navigate]);

  // Set the page title when dashboard loads
  useEffect(() => {
    if (dashboard) {
      setTitle(dashboard.title);
    }

    // Reset on unmount
    return () => {
      reset();
    };
  }, [dashboard, setTitle, reset]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-foreground">Dashboard not found</h2>
          <p className="mt-2 text-muted-foreground">
            The dashboard you're looking for doesn't exist or you don't have access to it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* mono Agent Input - hide when chat widget is open */}
      {!isChatOpen && (
        <div className="flex justify-center">
          <AgentInput />
        </div>
      )}

      {/* Header controls: Date Range, Edit, and Filters */}
      <div className="flex flex-col gap-3 md:flex-row md:justify-end md:items-center md:gap-3">
        {/* Mobile: Edit and Filters on second row, side-by-side */}
        {/* Desktop: Edit and Filters in control group */}
        <div className="flex flex-row items-center gap-2 md:gap-3">
          {/* Edit button */}
          <Button
            variant={isEditMode ? "default" : "outline"}
            size="sm"
            className="gap-2 min-h-[44px] md:min-h-0"
            onClick={() => setIsEditMode(!isEditMode)}
          >
            {isEditMode ? <Check className="size-4" /> : <Pencil className="size-4" />}
            <span className="hidden md:inline">{isEditMode ? "Done" : "Edit"}</span>
          </Button>
          {/* Filters button */}
          <FilterSheet dashboard={dashboard} />
        </div>
      </div>

      {/* Dashboard content - only render when filters are ready */}
      {filtersReady ? (
        <DashboardGrid
          dashboardId={dashboard.id}
          filterConfig={dashboard.filterConfig}
          isEditMode={isEditMode}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      )}
    </div>
  );
}
